/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import Peripherals from '@appblocks/codegeneration/Peripherals';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';


export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    const blocks: CodeBlock[] = [];

    const ENABLED = feature.getSettingValue('ENABLED');
    const name = feature.getSettingValue('NAME');
    let PORT = feature.getSettingValue('PORT');
    if (PORT.indexOf('/') === 0) {
        PORT = `{${PORT}}`;
    }
    const ID = feature.getSettingValue('ID');
    const tx = feature.getSettingValue('TX');
    const rx = feature.getSettingValue('RX');
    const rts = feature.getSettingValue('RTS');
    const cts = feature.getSettingValue('CTS');
    const BAUD = feature.getSettingValue('BAUD');

    if (!ENABLED) {
        blocks.push({
            location: 'APP_OVERLAY',
            blockType: BlockType.CODE,
            template: `
&${PORT} {
    status = "disabled";
};`,
        });
        return blocks;
    }
    if (!PORT) {
        return blocks;
    }

    let pinconfigCode = '';

    const pinctrlNames: string[] = ['"default"'];

    const device = PlatformUtils.getDeviceDefinition(feature.project.device.id);
    if (tx && rx) {
        if (device.soc.indexOf('esp') === 0) {
            pinconfigCode = `pinmux = <${PORT.toUpperCase()}_TX_GPIO${tx}>, 
        <${PORT.toUpperCase()}_RX_GPIO${rx}>;`;
        } else if (device.soc.indexOf('nrf') === 0) {
            const txPeripheral = PlatformUtils.getPeripheral(feature.project.device.id, tx, 'gpio');
            const rxPeripheral = PlatformUtils.getPeripheral(feature.project.device.id, rx, 'gpio');

            const txPort = txPeripheral.peripheralPort.replace('gpio', '');
            const rxPort = rxPeripheral.peripheralPort.replace('gpio', '');

            pinconfigCode = `psels = <NRF_PSEL(UART_TX, ${txPort}, ${txPeripheral.num})>,
                <NRF_PSEL(UART_RX, ${rxPort}, ${rxPeripheral.num})>;
        `;
            pinctrlNames.push('"sleep"');
        } else if (device.soc.indexOf('stm32') === 0) {
            const txPeripheral = PlatformUtils.getPeripheral(feature.project.device.id, tx, 'gpio');
            const rxPeripheral = PlatformUtils.getPeripheral(feature.project.device.id, rx, 'gpio');

            // pinctrl-0 = <&i2c1_scl_pb8 &i2c1_sda_pb9>;
            const txPinName = txPeripheral.peripheralPort.replace('gpio', '');
            const rxPinName = rxPeripheral.peripheralPort.replace('gpio', '');

            pinconfigCode = '';
            blocks.push({
                location: 'APP_OVERLAY',
                blockType: BlockType.CODE,
                template: `
&${PORT} {
    status = "okay";
    current-speed = <${BAUD}>;
    pinctrl-0 = <&${PORT}_tx_p${txPinName}${txPeripheral.num} &${PORT}_rx_p${rxPinName}${rxPeripheral.num}>;
    pinctrl-names = ${pinctrlNames.join(',')};
};`,
            });
        }
    }

    if (pinconfigCode !== '') {
        blocks.push({
            location: 'APP_OVERLAY_PINCTRL',
            blockType: BlockType.CODE,
            template: `
${name}_default: ${name}_default {
    group1 {
        ${pinconfigCode}
    };
};`,
        });

        blocks.push({
            location: 'APP_OVERLAY',
            blockType: BlockType.CODE,
            template: `
&${PORT} {
    status = "okay";
    current-speed = <${BAUD}>;
    pinctrl-names = ${pinctrlNames.join(',')};
    pinctrl-0 = <&${name}_default>;

};`,
        });
    } else {
        blocks.push({
            location: 'APP_OVERLAY',
            blockType: BlockType.CODE,
            template: `
&${PORT} {
    status = "okay";
    current-speed = <${BAUD}>;

};`,
        });
    }

    blocks.push({
        location: 'PRJ_CONF',
        blockType: BlockType.CODE,
        id: 'CONFIG_SERIAL=y',
        template: `
CONFIG_UART_INTERRUPT_DRIVEN=y
CONFIG_SERIAL=y
`,
    });

    blocks.push({
        location: 'GLOBAL_INCLUDES',
        blockType: BlockType.CODE,
        id: '#include <zephyr/drivers/uart.h>',
        template: `
#include <zephyr/drivers/uart.h>
`,
    });

    blocks.push({
        location: 'VARIABLE_DECLARATIONS',
        blockType: BlockType.CODE,
        template: `
static const struct device *const uart_dev_${name} = DEVICE_DT_GET(DT_NODELABEL(${PORT}));
`,
    });

    return blocks;
};
